import Table from "@/components/shared-ui/Table";
import type {
  MsSlaMetricRow,
  MsSlaPeriodValue,
  MsSlaUnits,
} from "@/services/api/types";
import { createColumnHelper } from "@tanstack/react-table";
import * as Styled from "./styled";
import { useMediaQuery } from "@/hooks/useMatchMedia";
import { statusColours } from "@/styles/statusColours";

type Props = {
  periods: string[];
  data: MsSlaMetricRow[];
};

const columnHelper = createColumnHelper<MsSlaMetricRow>();

const percentFormatter = new Intl.NumberFormat("en-GB", {
  style: "percent",
  maximumFractionDigits: 1,
});

const formatPercent = (value: number | null | "n/a") =>
  typeof value === "number" ? percentFormatter.format(value) : "N/A";

// Colours come from the RAG value set in the SLA table, not calculated here
const ragColour = (rag: string) => {
  switch (rag.trim().toLowerCase()) {
    case "green":
      return statusColours.green;
    case "amber":
      return statusColours.orange;
    case "red":
      return statusColours.red;
    default:
      return undefined;
  }
};

// Formats "2026-09" as "Sep 26", matching the column headers the page used before
const formatPeriodLabel = (label: string) => {
  const [year, month] = label.split("-").map(Number);
  if (!year || !month) return label;
  return new Date(year, month - 1).toLocaleDateString("en-GB", {
    month: "short",
    year: "2-digit",
  });
};

// Shows the latest target. If the target was different in earlier months, the
// earlier targets are listed underneath with the month they applied to.
const TargetCell = ({ row }: { row: MsSlaMetricRow }) => {
  // RAG-only metrics have no target
  if (row.units === "rag") {
    return null;
  }

  return (
    <>
      <span>{formatPercent(row.target)}</span>
      {row.targetChanges.length > 0 && (
        <Styled.TargetChange>
          Target changed from{" "}
          {row.targetChanges
            .map(
              (change) =>
                `${formatPercent(change.target)} (${formatPeriodLabel(change.period)})`,
            )
            .join(", ")}
        </Styled.TargetChange>
      )}
    </>
  );
};

// RAG-only metrics show the RAG label rather than a percentage
const ActualCell = ({
  value,
  units,
}: {
  value?: MsSlaPeriodValue | null;
  units: MsSlaUnits;
}) => {
  if (!value) {
    return <span>–</span>;
  }

  const colour = ragColour(value.rag);

  return (
    <Styled.SLAColor color={colour?.backgroundColour}>
      {colour && <Styled.ProgressCircle color={colour.colour} />}
      <span>{units === "rag" ? value.rag : formatPercent(value.actual)}</span>
    </Styled.SLAColor>
  );
};

const SLATable = ({ periods, data }: Props) => {
  const isDesktop = useMediaQuery("large");

  const metricColumn = columnHelper.accessor("metric", {
    header: () => "Metric",
    cell: (info) => info.getValue(),
    size: isDesktop ? 0 : 300,
  });

  const targetColumn = columnHelper.display({
    id: "target",
    header: () => "Target",
    cell: (info) => <TargetCell row={info.row.original} />,
    size: isDesktop ? 130 : 100,
  });

  const periodColumns = periods.map((period) =>
    columnHelper.accessor((row) => row.values[period], {
      id: period,
      header: () => (
        <span
          style={{ width: "20px", whiteSpace: "break-spaces", display: "flex" }}
        >
          {formatPeriodLabel(period)}
        </span>
      ),
      cell: (info) => (
        <ActualCell value={info.getValue()} units={info.row.original.units} />
      ),
      size: isDesktop ? 130 : 100,
    }),
  );

  return (
    <Table
      columns={[metricColumn, targetColumn, ...periodColumns]}
      data={data}
      fixedLayout
    />
  );
};

export default SLATable;
